import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from '../../../shared/components/ui';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { EstimateTreeEditor } from './EstimateTreeEditor';

export function EditEstimate() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Get initial view mode from navigation state (passed from EstimateDetail)
  const initialViewMode = location.state?.viewMode || 'modern';
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        setLoading(true);
        const data = await estimatesApi.getEstimateById(estimateId);

        // Only allow editing draft estimates (status undefined or 'draft' is allowed)
        if (data.status && data.status !== 'draft') {
          setError('Only draft estimates can be edited. This estimate is marked as complete.');
          return;
        }

        setEstimate(data);
      } catch (err) {
        console.error('Failed to load estimate:', err);
        setError('Failed to load estimate. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [estimateId]);

  const handleSave = async (updatedEstimate) => {
    try {
      await estimatesApi.updateEstimate(estimateId, updatedEstimate);
      navigate(`/estimates/${estimateId}`);
    } catch (err) {
      console.error('Failed to save estimate:', err);
      throw err; // Let EstimateTreeEditor handle the error display
    }
  };

  const handleCancel = () => {
    navigate(`/estimates/${estimateId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading estimate...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Cannot Edit Estimate</h3>
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={() => navigate(`/estimates/${estimateId}`)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!estimate) {
    return null;
  }

  return (
    <EstimateTreeEditor
      initialEstimate={estimate}
      projectName={estimate.projectName}
      onSave={handleSave}
      onCancel={handleCancel}
      initialViewMode={initialViewMode}
    />
  );
}

export default EditEstimate;
